import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    this.#scheduleVideoPreview();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    if (this.#videoPreviewTimer != null) {
      window.clearTimeout(this.#videoPreviewTimer);
      this.#videoPreviewTimer = null;
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /** @type {number | null} */
  #videoPreviewTimer = null;

  /**
   * Start a selected product video immediately. This keeps a thumbnail click
   * from leaving the user on a static, coloured poster image.
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated && this.#videoPreviewTimer != null) {
      window.clearTimeout(this.#videoPreviewTimer);
      this.#videoPreviewTimer = null;
    }

    const video = event.detail.slide?.querySelector('deferred-media');
    if (!(video instanceof HTMLElement)) return;

    this.#startVideo(video);
  };

  /**
   * Gently move a product gallery to its video preview when it is available.
   * A user interaction always cancels this, so it never overrides a deliberate
   * thumbnail selection.
   */
  #scheduleVideoPreview() {
    const slideshow = this.slideshow;
    const slides = slideshow?.refs.slides;
    const videoIndex = slides?.findIndex((slide) => slide.querySelector('deferred-media')) ?? -1;

    if (!slideshow || videoIndex < 0) return;

    this.#videoPreviewTimer = window.setTimeout(() => {
      this.#videoPreviewTimer = null;
      const videoSlide = slideshow.refs.slides?.[videoIndex];
      const video = videoSlide?.querySelector('deferred-media');
      if (!(video instanceof HTMLElement)) return;

      this.#startVideo(video);

      if (slideshow.current === videoIndex) {
        return;
      } else {
        void slideshow.select(videoIndex, undefined, { animate: false });
      }
    }, 2200);
  }

  /**
   * Load and play a deferred video without moving focus away from the gallery.
   *
   * @param {HTMLElement} video
   */
  #startVideo(video) {
    if (typeof video.loadContent !== 'function') return;

    video.loadContent(false);
    video.playMedia?.();
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
